import { baseApi } from './baseApi';
import i18n from 'i18next';

const subscriptionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchSubscriptionStatus: builder.query({
      query: () => ({
        url: '/permission-status',
        method: 'GET',
      }),
      transformResponse: (data) => {
        // Map subscription_credits.details to subscriptions format for backward compatibility
        // details has a unified structure with extra_credits and one_time_credits
        const mapDetailsToSubscriptions = (details, existingSubs = []) => {
          if (!details || !Array.isArray(details)) return [];
          return details.map((detail) => {
            // Try to find matching subscription in existingSubs to get more information
            const matchingSub = existingSubs.find(
              (sub) =>
                sub.sub_id === detail.subscription_id ||
                sub.subscription_id === detail.subscription_id,
            );

            // Calculate frequency based on date difference
            let subFreq = null;
            let periodType = null;
            if (detail.period_start && detail.period_end) {
              const start = new Date(detail.period_start);
              const end = new Date(detail.period_end);
              const diffMonths =
                (end.getFullYear() - start.getFullYear()) * 12 +
                (end.getMonth() - start.getMonth());
              if (diffMonths <= 1) {
                subFreq = 'month';
                periodType = 'month';
              } else {
                subFreq = 'year';
                periodType = 'year';
              }
            }

            return {
              sub_type: detail.sub_type || matchingSub?.sub_type || null,
              sub_freq: matchingSub?.sub_freq || subFreq || 'month',
              sub_id: detail.subscription_id || matchingSub?.sub_id || null,
              period_start: detail.period_start || matchingSub?.period_start,
              period_end: detail.period_end || detail.expires || matchingSub?.period_end,
              trial_start: matchingSub?.trial_start || null,
              trial_end: matchingSub?.trial_end || null,
              cancel_at_period_end: matchingSub?.cancel_at_period_end || false,
              has_paid: matchingSub?.has_paid ?? true,
              free_credits: matchingSub?.free_credits || 0,
              stripe_user: data.subscription_credits?.stripe_user ?? false,
              creds: detail.credits_remaining || matchingSub?.creds || 0,
              periodType: matchingSub?.periodType || periodType || 'month',
              currency: matchingSub?.currency || 'pln',
            };
          });
        };

        // Use subscriptions if available (for full information), then details, then current_subs
        const subscriptionSubs = data.subscription_credits?.subscriptions;
        const subscriptionDetails = data.subscription_credits?.details;
        const legacySubs = data.current_subs || [];

        // If we have subscriptions, use them directly (they have more information)
        // If we only have details, map them using subscriptions as reference
        const allSubs =
          subscriptionSubs && subscriptionSubs.length > 0
            ? subscriptionSubs
            : subscriptionDetails && subscriptionDetails.length > 0
              ? mapDetailsToSubscriptions(subscriptionDetails, subscriptionSubs || legacySubs)
              : legacySubs;

        return {
          ...data,
          // Backward compatibility - mapping to current_subs
          current_subs: allSubs.map((sub) => ({
            ...sub,
            periodType:
              sub.periodType || (sub.sub_freq?.toLowerCase().includes('month') ? 'month' : 'year'),
            currency: sub.currency === 'pln' ? 'zł' : sub.currency,
          })),
          // Keep old fields for backward compatibility
          counts: data.counts ?? data.total_used ?? 0,
          creds:
            data.creds ??
            (data.subscription_credits?.total ?? 0) + (data.one_time_credits?.total ?? 0),
          sub_creds: data.sub_creds ?? data.subscription_credits?.total ?? 0,
          // extra_credits can be a number (old API) or an object (new API)
          extra_credits:
            typeof data.extra_credits === 'number'
              ? data.extra_credits
              : data.extra_credits_old ?? data.extra_credits?.total ?? 0,
          currentSubs: data.currentSubs ?? allSubs,
          stripe_user: data.stripe_user ?? data.subscription_credits?.stripe_user ?? false,
        };
      },
    }),

    fetchProducts: builder.query({
      query: ({ getAllProducts }) => ({
        url: '/stripe/subscribable-product',
        method: 'GET',
        headers: getAllProducts ? { Authorization: null } : {}, // Skip Authorization header to fetch all products
      }),
      transformResponse: (data) =>
        data.map((product) => ({
          ...product,
          periodType:
            product.freq === null
              ? 'one-off'
              : product.freq.toLowerCase().includes('month')
                ? 'month'
                : 'year',
          currency: product.currency === 'pln' ? 'zł' : product.currency,
        })),
    }),

    fetchCounter: builder.query({
      query: ({ offset, limit }) => ({
        url: `/counter?limit=${limit}&offset=${offset}`,
        method: 'GET',
      }),
    }),

    fetchStripePortal: builder.mutation({
      query: () => ({
        url: '/stripe/customer-portal/', // on purpose use of '/' at the end
        method: 'POST',
      }),
    }),

    // Endpoint for subscription products
    fetchStripeCheckout: builder.mutation({
      query: ({ price_id }) => ({
        url: '/stripe/checkout/', // on purpose use of '/' at the end
        method: 'POST',
        body: { price_id },
        credentials: 'include',
      }),
      transformResponse: (response) => response,
    }),

    // Endpoint for one-off products
    fetchOneTimeCheckout: builder.mutation({
      query: ({ price_id }) => {
        const currentLanguage = i18n.language || 'pl';
        return {
          url: '/payments/one-time-purchases/create_checkout_session/',
          method: 'POST',
          body: {
            price_id,
            language: currentLanguage,
          },
          credentials: 'include',
        };
      },
      transformResponse: (response) => {
        // The one-time purchase endpoint might return a different structure
        // It could include fields like product, amount_paid, currency, credits_added, status
        // We need to transform it to a consistent format
        // If the response already has a session_id or checkout_url, return it as is
        if (response.session_id || response.checkout_url || response.redirect_url) {
          return response;
        }
        // Otherwise, try to extract the relevant information
        return {
          ...response,
          // Add any necessary transformations here
        };
      },
    }),

    consentConsumerRights: builder.mutation({
      query: (consent) => ({
        url: '/consumer-rights-consent/', // on purpose use of '/' at the end
        method: 'POST',
        body: { consent },
      }),
    }),
  }),
});

export const {
  useFetchSubscriptionStatusQuery,
  useFetchProductsQuery,
  useFetchCounterQuery,
  useFetchStripePortalMutation,
  useFetchStripeCheckoutMutation,
  useFetchOneTimeCheckoutMutation,
  useConsentConsumerRightsMutation,
} = subscriptionApi;
